import { getNotationStore } from "@/lib/music/notation-store";
import { Application, Container } from "pixi.js";
import { Engine } from "../../engine/engine";
import { PianoComponent } from "./components/piano-component";
import type { KeySpan } from "@/lib/harmony/key-spans";
import { PianoRollComponent, type ColorTokenResolver, type LiveLaneMode, type RecordZone } from "./components/piano-roll-component";
import { MinimapComponent, minimapWidthFor } from "./components/minimap-component";
import type { NoteEffectStyle } from "@/lib/engine/components/piano-roll/note-effects/types";
import { HintSelection } from "./hint-selection";
import { getFpsWatchdog } from "@/lib/perf/fps-watchdog";
import { ALL_SURFACES, type StudioSurface } from "../canvas-cover";
import { ViewNavigator } from "./view-navigator";
import { EMPTY_LAYOUT, type SequenceLayout } from "./sequence-layout";
import { isWitnessKeyboard, WITNESS_KEYBOARD_HEIGHT } from "./witness-keyboard";
import { Piano } from "../../piano/piano";
import { LocalTransport } from "../../track/local-transport";
import { TrackManager } from "../../track/track-manager";

export class StudioView {
  private engine: Engine;
  private app: Application | null = null;
  private pianoComponent: PianoComponent | null = null;
  private pianoRollComponent: PianoRollComponent | null = null;
  private minimapComponent: MinimapComponent | null = null;
  private pianoContainer: Container | null = null;
  private pianoRollContainer: Container | null = null;
  private minimapContainer: Container | null = null;
  private hintSelection = new HintSelection();
  private viewNavigator: ViewNavigator;
  private resizeHandler: ((width: number, height: number) => void) | null = null;
  /**
   * Dernière mesure publiée, pour n'émettre que sur changement.
   *
   * `-1` et non `1` : la première frame doit publier, même sur un morceau
   * qui démarre à la mesure 1.
   */
  private lastBar = -1;
  private measureListeners = new Set<(measure: number) => void>();
  /**
   * La zone d'enregistrement, posée dans la règle du roll.
   *
   * Gardée ici en plus du composant : la vue se reconstruit (changement de
   * morceau, remontage du canvas), et des bornes que le joueur vient de poser
   * ne doivent pas disparaître avec le conteneur qui les dessinait.
   */
  private recordZone: RecordZone | null = null;
  /** La pose des bornes est-elle ouverte au joueur ? Voir le composant. */
  private recordZoneEditable = false;
  private recordZoneListeners = new Set<(zone: RecordZone | null) => void>();
  private frameListeners = new Set<(tick: number) => void>();

  /**
   * Ce qu'un calque monté cache, et que la vue cesse donc de dessiner.
   *
   * Voir `canvas-cover.ts` pour qui déclare quoi. Ici on ne fait qu'obéir :
   * une surface cachée voit son conteneur éteint — Pixi saute alors sa branche
   * entière au rendu — et son travail d'image sauté.
   */
  private hidden: ReadonlySet<StudioSurface> = new Set();

  private transportUnsub: (() => void) | null = null;
  private notationUnsub: (() => void) | null = null;
  private pianoUnsub: (() => void) | null = null;

  constructor(
    container: HTMLElement,
    private piano: Piano,
    private localTransport: LocalTransport,
    private trackManager: TrackManager,
    private getColorToken: ColorTokenResolver,
    private onKeyboardHeight: (height: number) => void,
    private onMinimapWidth: (width: number) => void,
    private onPlayheadDistance: (distance: number) => void,
    /**
     * La séquence de morceaux, vue depuis le rouleau. Lue par le roll et la
     * minimap ; `EMPTY_LAYOUT` sans séquence, et rien ne change pour eux.
     */
    private sequence: SequenceLayout = EMPTY_LAYOUT,
  ) {
    this.engine = new Engine(container);
    this.viewNavigator = new ViewNavigator(localTransport);
  }

  async start(): Promise<Application> {
    await this.engine.start();
    this.app = await this.engine.getApplication();

    return this.app;
  }

  async build() {
    if (!this.app) {
      throw new Error("Cannot build StudioView before starting the engine");
    }

    const appStage = this.app.stage;

    // Render Group: centerContainer acts as its own mini scene graph.
    // PixiJS offloads transform/tint/alpha to the GPU for the group,
    // reducing CPU work when children haven't changed structurally.
    const centerContainer = new Container({ isRenderGroup: true });

    const pianoContainer = new Container();
    this.pianoComponent = new PianoComponent(pianoContainer, this.piano);

    // Enable culling on the piano roll so off-screen notes are skipped
    const pianoRollContainer = new Container();
    pianoRollContainer.cullable = true;
    pianoRollContainer.cullableChildren = true;

    this.pianoRollComponent = new PianoRollComponent(
      pianoRollContainer,
      this.pianoComponent,
      this.pianoComponent.layout,
      this.localTransport,
      this.trackManager,
      this.viewNavigator,
      this.getColorToken,
      this.piano,
      this.sequence,
    );
    this.pianoRollComponent.setHintSelection(this.hintSelection);
    this.pianoRollComponent.onRecordZoneChange((zone) => {
      this.recordZone = zone;
      this.recordZoneListeners.forEach((listener) => listener(zone));
    });
    this.pianoRollComponent.setRecordZone(this.recordZone);
    this.pianoRollComponent.setRecordZoneEditable(this.recordZoneEditable);

    // Render Group for the minimap (independent from the center area)
    const minimapContainer = new Container({ isRenderGroup: true });
    this.minimapComponent = new MinimapComponent(
      minimapContainer,
      this.localTransport,
      this.trackManager,
      this.viewNavigator,
      this.getColorToken,
      this.sequence,
    );
    // Pass the piano roll visible tick range so the minimap viewport rect is accurate
    this.minimapComponent.setVisibleTickRange(960);
    this.minimapComponent.build();

    // Keep container refs so a viewport change can re-run the layout without a
    // canvas resize (see relayout()).
    this.pianoContainer = pianoContainer;
    this.pianoRollContainer = pianoRollContainer;
    this.minimapContainer = minimapContainer;

    // Local refs for closures
    const pianoComponent = this.pianoComponent;
    const pianoRollComponent_ = this.pianoRollComponent;
    const minimapComponent_ = this.minimapComponent;

    // Z-order: piano roll first (behind), piano on top (in front), minimap separate
    appStage.addChild(centerContainer);
    centerContainer.addChild(pianoRollContainer);
    centerContainer.addChild(pianoContainer);
    appStage.addChild(minimapContainer);

    // Initial layout
    this.doLayout(
      pianoContainer,
      pianoRollContainer,
      minimapContainer,
      pianoComponent,
      pianoRollComponent_,
      minimapComponent_,
      this.app.screen.width,
      this.app.screen.height
    );

    // Layout on resize
    this.resizeHandler = (width: number, height: number) => {
      this.doLayout(
        pianoContainer,
        pianoRollContainer,
        minimapContainer,
        pianoComponent,
        pianoRollComponent_,
        minimapComponent_,
        width,
        height
      );
    };

    this.app.renderer.on("resize", this.resizeHandler);

    // Register the update loop on the Pixi ticker.
    // Draw the roll BEFORE the keyboard: the roll's auto-follow updates the
    // shared horizontal offset, and the keyboard then applies that same offset
    // to its container in the same frame, keeping them pixel-aligned.
    this.pianoRollComponent.draw();
    this.pianoComponent.draw();
    this.engine.registerLoop((ticker) => {
      /*
        Le roll se dessine encore quand seul son conteneur est éteint, et c'est
        voulu : c'est son auto-suivi qui met à jour le décalage horizontal
        partagé, que le clavier applique au sien dans la même image. Le sauter
        pendant que le clavier se voit décrocherait les deux d'un pixel par
        image. On ne le saute donc que lorsque le clavier est caché lui aussi —
        c'est-à-dire quand plus personne ne lit ce décalage.
      */
      if (!this.hidden.has("piano") || !this.hidden.has("roll")) {
        this.pianoRollComponent?.draw();
      }

      if (!this.hidden.has("piano")) {
        this.pianoComponent?.draw();
      }

      if (!this.hidden.has("minimap")) {
        this.minimapComponent?.update();
      }

      // Le tick, lui, part toujours : des vues DOM s'y posent — la partition du
      // parcours harmonique la première — et c'est précisément pendant qu'elles
      // couvrent le canvas qu'elles en ont besoin.
      this.publishMeasure();
      this.publishFrame();

      // Feed the FPS watchdog only the frames that actually render. When the
      // ticker parks at idle it stops sampling — no independent rAF keeps the
      // frame pipeline awake (see FpsWatchdog).
      if (!this.blind()) {
        getFpsWatchdog().sample(ticker.deltaMS);
      }
    });

    // Render on demand: keep the ticker awake only while something moves — the
    // transport is playing (notes scroll), the navigator is mid-gesture /
    // inertia / scroll-to, or the roll is animating an effect of its own (a
    // held key's halo and sparks, or the particles still falling after the
    // release). Otherwise the Engine governor parks it.
    this.engine.setActivityProbe(
      () => !this.localTransport.isDetached
        || this.viewNavigator.isBusy
        || (this.pianoRollComponent?.isAnimating() ?? false),
    );

    // Transport play / pause / seek must wake the renderer: when parked, the
    // governor isn't ticking to notice playback resumed, and a pause/seek needs
    // its final frame drawn.
    this.transportUnsub = this.localTransport.on(() => this.engine.requestRender());

    // Note-name notation (Do Ré Mi vs C D E) is baked into the hint rows when
    // they are built, so a change has to invalidate them AND wake the renderer:
    // parked at idle, nothing else would redraw the stale labels.
    this.notationUnsub = getNotationStore().subscribe(() => {
      this.pianoRollComponent?.invalidateHintLabels();
      this.engine.requestRender();
    });

    // Piano state changes redraw into the scene graph synchronously
    // (`PianoRenderer` reacts to `hints_changed` / `press` / `release`), but
    // nothing presents that frame while the ticker is parked. Sources that
    // don't touch the canvas — hints published by React, MIDI or computer
    // keyboard input — would stay invisible until an unrelated pointer/wheel
    // event on the canvas woke the engine. Wake it here instead.
    this.pianoUnsub = this.piano.on(() => this.engine.requestRender());

    // Present the freshly built scene even if it parks before the first idle tick.
    this.engine.requestRender();
  }

  /**
   * Poser ce qui est caché, et en tirer ce qu'il reste à faire.
   *
   * Deux gestes, et ils ne portent pas sur la même chose. Éteindre un conteneur
   * retire sa branche du rendu : Pixi ne la parcourt plus, et le GPU ne la
   * dessine plus. Sauter son travail d'image, dans la boucle, retire le calcul
   * qui l'aurait alimentée. Le premier sans le second laisserait le processeur
   * mettre à jour des sprites que personne ne regarde.
   *
   * Quand les trois surfaces sont cachées, il n'y a plus d'image à produire du
   * tout, et c'est le seul cas où l'on cesse de présenter. Tant qu'une seule se
   * voit — la minimap, sous le parcours harmonique — une image doit sortir.
   */
  setHiddenSurfaces(hidden: ReadonlySet<StudioSurface>): void {
    this.hidden = hidden;

    if (this.pianoRollContainer !== null) {
      this.pianoRollContainer.visible = !hidden.has("roll");
    }

    if (this.pianoContainer !== null) {
      this.pianoContainer.visible = !hidden.has("piano");
    }

    if (this.minimapContainer !== null) {
      this.minimapContainer.visible = !hidden.has("minimap");
    }

    if (this.blind()) {
      this.engine.setPresenting(false);

      return;
    }

    // Ce qui vient d'être découvert doit se repeindre, même à l'arrêt : le
    // gouverneur, parké, ne le remarquerait pas. `setPresenting(true)` le
    // demande déjà quand on revient d'un recouvrement complet ; il reste à le
    // demander quand seule une partie se découvre.
    this.engine.setPresenting(true);
    this.engine.requestRender();
  }

  /** Le canvas est-il entièrement caché ? */
  private blind(): boolean {
    return ALL_SURFACES.every((surface) => this.hidden.has(surface));
  }

  /**
   * Position the piano, piano roll, and minimap containers.
   * Layout (left to right): piano roll | minimap
   */
  private doLayout(
    pianoContainer: Container,
    pianoRollContainer: Container,
    minimapContainer: Container,
    pianoComponent: PianoComponent,
    pianoRollComponent: PianoRollComponent,
    minimapComponent: MinimapComponent,
    width: number,
    height: number
  ): void {
    // Le rail se resserre sur un canvas étroit : autant de px rendus au
    // clavier et au roll, qui sont l'écran principal.
    const minimapWidth = minimapWidthFor(width);
    const centerWidth = width - minimapWidth;

    /*
      Sur un téléphone couché, le clavier n'est plus qu'un témoin : il montre
      ce qui sonne et cesse de prétendre qu'on va y jouer. Voir
      `witness-keyboard.ts`, où le pourquoi et le prix sont écrits.

      Le média est relu à chaque layout, jamais mémorisé : une rotation passe
      par un redimensionnement, donc par ici, et le clavier reprend sa taille
      pleine au retour sans qu'aucun état ait à être resynchronisé.

      Posé **avant** de mesurer : c'est lui qui décide des bornes de largeur de
      touche, dont la bande tire sa hauteur hors témoin.
    */
    const witness = isWitnessKeyboard();

    pianoComponent.setWitness(witness);

    // Keyboard strip height tracks the clamped white-key width (constant key
    // aspect). Derived from the available width only — no dependency on
    // `height` — so there is no circularity with the roll's height below.
    const pianoHeight = witness
      ? WITNESS_KEYBOARD_HEIGHT
      : pianoComponent.keyboardHeightForWidth(centerWidth);
    const rollScreenHeight = height - pianoHeight;

    // Piano (keyboard)
    pianoContainer.x = 0;
    pianoContainer.y = height - pianoHeight;
    pianoComponent.setSize(centerWidth, pianoHeight);

    // Piano roll, fills available space to the left
    pianoRollContainer.x = 0;
    pianoRollContainer.y = 0;
    pianoRollComponent.setSize(centerWidth, rollScreenHeight);

    // Minimap, right edge, full height
    minimapContainer.x = width - minimapWidth;
    minimapContainer.y = 0;
    minimapComponent.resize(minimapWidth, height);

    // Publish the live keyboard height so React overlays clamp exactly to the
    // top of the keyboard (no uncovered roll band when keys shrink).
    this.onKeyboardHeight(pianoHeight);

    // Même contrat pour la largeur du rail : les overlays React s'arrêtent au
    // bord de la minimap, qui n'est plus une constante. Sans cette publication
    // ils la recopieraient et divergeraient au premier redimensionnement.
    this.onMinimapWidth(minimapWidth);

    // Idem pour la playhead, qui redescend vers le piano quand le roll manque
    // de hauteur. Lue après `setSize` : c'est la distance réellement rendue,
    // pas un second calcul de la même règle.
    this.onPlayheadDistance(pianoRollComponent.playheadDistance);
  }

  /**
   * Re-run the layout against the current canvas size. Called after a viewport
   * change (load / transpose / recentre) so the keyboard strip height tracks
   * the new clamped key width even without a canvas resize.
   */
  relayout(): void {
    if (
      !this.app ||
      !this.pianoContainer ||
      !this.pianoRollContainer ||
      !this.minimapContainer ||
      !this.pianoComponent ||
      !this.pianoRollComponent ||
      !this.minimapComponent
    ) {
      return;
    }

    this.doLayout(
      this.pianoContainer,
      this.pianoRollContainer,
      this.minimapContainer,
      this.pianoComponent,
      this.pianoRollComponent,
      this.minimapComponent,
      this.app.screen.width,
      this.app.screen.height,
    );

    this.engine.requestRender();
  }

  setAutoHintSelect(enabled: boolean): void {
    this.hintSelection.setAutoSelect(enabled);
    this.engine.requestRender();
  }

  get autoHintSelect(): boolean {
    return this.hintSelection.autoSelect;
  }

  setHighlightActiveBeat(enabled: boolean): void {
    this.pianoRollComponent?.setHighlightActiveBeat(enabled);
    this.engine.requestRender();
  }

  /**
   * Le délai de restitution du transport vient de changer.
   *
   * Aucun nombre ne passe ici : le roll lit le délai sur le transport local,
   * le seul endroit où il vit. Mais le transport n'émet rien à ce moment-là,
   * à dessein (voir `LocalTransport.setPlayoutDelay`), et un roll parqué ne
   * redessine que ce qui bouge — sans ce réveil, la ligne du présent
   * resterait à l'ancienne hauteur jusqu'au prochain mouvement.
   */
  playoutDelayChanged(): void {
    this.pianoRollComponent?.playoutDelayChanged();
    this.engine.requestRender();
  }

  setWorkRange(range: { startTick: number; endTick: number } | null): void {
    this.pianoRollComponent?.setWorkRange(range);
    this.engine.requestRender();
  }

  /** The key spans of the piece, drawn on the roll's right edge. */
  setKeySpans(spans: readonly KeySpan[]): void {
    this.pianoRollComponent?.setKeySpans(spans);
    this.engine.requestRender();
  }

  /**
   * Poser ou retirer la zone d'enregistrement.
   *
   * `null` l'enlève. Les ticks sont calés sur des demi-temps par le roll
   * lui-même quand c'est le joueur qui les pose ; posés d'ici, ils sont pris
   * tels quels.
   */
  setRecordZone(zone: RecordZone | null): void {
    this.recordZone = zone;
    this.pianoRollComponent?.setRecordZone(zone);
    this.engine.requestRender();
  }

  /** La zone d'enregistrement, ou `null`. */
  getRecordZone(): RecordZone | null {
    return this.recordZone;
  }

  /**
   * Ouvrir ou fermer la pose des bornes dans la règle. Éteint par défaut.
   *
   * La règle, elle, reste lisible dans les deux cas : c'est la pose qu'on
   * ouvre, pas la graduation.
   */
  setRecordZoneEditable(editable: boolean): void {
    this.recordZoneEditable = editable;
    this.pianoRollComponent?.setRecordZoneEditable(editable);
    this.engine.requestRender();
  }

  /** S'abonner aux bornes posées ou déplacées. Rend le désabonnement. */
  onRecordZoneChange(listener: (zone: RecordZone | null) => void): () => void {
    this.recordZoneListeners.add(listener);

    return () => {
      this.recordZoneListeners.delete(listener);
    };
  }

  setLiveLaneMode(mode: LiveLaneMode): void {
    this.pianoRollComponent?.setLiveLaneMode(mode);
    // The ticker parks when the transport is paused and nothing is animating,
    // which is exactly the state the trace is read in: without this the roll
    // would keep presenting the frame it parked on.
    this.engine.requestRender();
  }

  /**
   * Présenter le roll après que la piste vive a été vidée.
   *
   * Rien de ce que les garde-fous observent n'a bougé, et le ticker est
   * parqué dès que le transport est en pause : sans ce réveil, les notes
   * effacées resteraient à l'écran jusqu'au prochain geste.
   */
  refreshClearedNotes(): void {
    this.pianoRollComponent?.invalidateNotes();
    this.minimapComponent?.markNotesDirty();
    this.engine.requestRender();
  }

  setNoteEffectStyle(style: NoteEffectStyle): void {
    this.pianoRollComponent?.setNoteEffectStyle(style);
    this.engine.requestRender();
  }

  setNoteDust(enabled: boolean): void {
    this.pianoRollComponent?.setNoteDust(enabled);
    this.engine.requestRender();
  }

  setMeasureLinesVisible(visible: boolean): void {
    this.pianoRollComponent?.setMeasureLinesVisible(visible);
    this.engine.requestRender();
  }

  /**
   * Hauteur (px) occupée au bas du roll par un élément DOM posé dessus, que la
   * playhead doit surplomber.
   *
   * `relayout` derrière, et non un simple `requestRender` : la distance de
   * playhead est publiée vers React à chaque layout, et les overlays qui la
   * lisent doivent apprendre celle-ci comme les autres.
   */
  setFootReserve(px: number): void {
    this.pianoRollComponent?.setFootReserve(px);
    this.relayout();
    this.engine.requestRender();
  }

  /**
   * Manually select notes in the piano-roll. The renderer dims notes
   * not part of the selection (alpha 0.15) so the active notes pop.
   *
   * `maxOnsetTick` defaults to `tick` (single-onset selection). Pass a
   * larger value for chords whose notes span a tick range.
   */
  setHintSelection(midis: readonly number[], tick: number, maxOnsetTick?: number): void {
    this.hintSelection.set(
      [...midis],
      tick,
      maxOnsetTick ?? tick,
    );
    this.engine.requestRender();
  }

  clearHintSelection(): void {
    this.hintSelection.clear();
    this.engine.requestRender();
  }

  /** Effective visual tick (transport tick + scroll offset). */
  getEffectiveTick(): number {
    return this.viewNavigator.getEffectiveTick();
  }

  /**
   * Subscribe to the player scrolling the roll and letting go. Silent while
   * the application scrolls it itself. See `ViewNavigator.onScrub`.
   */
  onScrub(listener: (tick: number) => void): () => void {
    return this.viewNavigator.onScrub(listener);
  }

  /** Smoothly scroll the piano roll to a target tick (spring animation). */
  /**
   * Les verbes du geste de navigation, tels que le roll les emploie.
   *
   * Exposés pour qu'une surface **hors du canvas** puisse conduire la même
   * navigation : la partition du parcours harmonique s'en sert pour que le
   * doigt qui la fait défiler déplace la tête de lecture du studio, et non un
   * décalage qui lui appartiendrait. Deux gestionnaires de geste, chacun avec
   * son inertie et son commit, dériveraient l'un de l'autre au premier réglage.
   *
   * Ce sont des délégations nues, sans logique ajoutée : tout ce qui décide —
   * la mise en pause pendant le geste, l'inertie, le `seekToTick` final — reste
   * dans `ViewNavigator`, et il n'y en a qu'un.
   *
   * ## Chacune réveille le rendu, et c'est tout ce qu'elles ajoutent
   *
   * Le gouverneur garde le ticker en vie tant que le navigateur est occupé,
   * mais **parké, il ne tourne pas pour s'en apercevoir**. Le canvas s'en sort
   * parce que ses propres écouteurs de pointeur et de molette le réveillent
   * (voir `Engine`) ; un geste venu d'ailleurs n'a personne pour le faire.
   * Sans ce réveil, un glissé sur une surface React ne se voyait qu'au
   * relâchement, quand le `seekToTick` final finissait par réveiller tout le
   * monde : le décalage bougeait, et rien ne le dessinait.
   */
  beginViewGesture(): void {
    this.viewNavigator.beginGesture();
    this.engine.requestRender();
  }

  updateViewOffset(deltaTicks: number): void {
    this.viewNavigator.updateOffset(deltaTicks);
    this.engine.requestRender();
  }

  endViewGesture(immediate = false): void {
    this.viewNavigator.endGesture(immediate);
    this.engine.requestRender();
  }

  wheelViewOffset(deltaTicks: number): void {
    this.viewNavigator.handleWheelDelta(deltaTicks);
    this.engine.requestRender();
  }

  /**
   * Voir `ViewNavigator.setLocked`. Le rendu est réveillé parce que fermer
   * peut terminer un geste en cours, donc déplacer la vue.
   */
  setNavigationLocked(locked: boolean): void {
    this.viewNavigator.setLocked(locked);
    this.engine.requestRender();
  }

  /** Voir `ViewNavigator.seekWithoutJump`. */
  seekWithoutJump(tick: number): void {
    this.viewNavigator.seekWithoutJump(tick);
    this.engine.requestRender();
  }

  /**
   * Voir `ViewNavigator.setFloorTick`. Réveillé parce que remonter le
   * plancher peut rogner un décalage en cours, donc déplacer la vue.
   */
  setNavigationFloor(tick: number): void {
    this.viewNavigator.setFloorTick(tick);
    this.engine.requestRender();
  }

  /**
   * La séquence de morceaux a changé : décalages, frontières, morceau
   * courant. Le rouleau et le rail relisent leur mise en page, et l'image
   * est demandée parce qu'un morceau ajouté derrière le courant ne bouge
   * rien de ce qui réveille le rendu de lui-même.
   */
  sequenceChanged(): void {
    this.pianoRollComponent?.sequenceChanged();
    this.minimapComponent?.markNotesDirty();
    this.engine.requestRender();
  }

  animateScrollToTick(tick: number, durationMs?: number): Promise<void> {
    // Wake the renderer up front: the navigator drives the ease on its own rAF,
    // but the ticker must run to present the moving offset. `isBusy` then keeps
    // it alive for the duration of the animation.
    this.engine.requestRender();

    return this.viewNavigator.animateScrollToTick(tick, durationMs);
  }

  /**
   * S'abonner au changement de mesure. Rend la fonction de désabonnement.
   *
   * Publié depuis la boucle de rendu, et non par un `setInterval` : le ticker
   * ne tourne que quand quelque chose bouge (lecture, geste de navigation,
   * animation), et rien d'autre ne peut déplacer le tick effectif. Au repos
   * il n'y a donc rien à publier, et aucun réveil à provoquer pour s'en
   * apercevoir.
   *
   * Couvre la lecture et le défilement sans les distinguer : les deux
   * passent par `getEffectiveTick()`.
   */
  onMeasureChange(callback: (measure: number) => void): () => void {
    this.measureListeners.add(callback);

    return () => {
      this.measureListeners.delete(callback);
    };
  }

  /**
   * S'abonner au tick effectif, image par image. Rend le désabonnement.
   *
   * Le pendant continu de `onMeasureChange`, et il vit au même endroit pour la
   * même raison : c'est la boucle de rendu qui connaît la position dessinée, et
   * elle seule. Une vue qui voudrait suivre le roll au tick près sans passer
   * par ici devrait ouvrir sa propre boucle, c'est-à-dire exactement ce que le
   * gouverneur de rendu a été écrit pour supprimer.
   *
   * Le tick n'est **pas** celui du transport mais celui que le roll dessine :
   * pendant un geste de navigation ils diffèrent de l'offset visuel, et c'est
   * le dessiné qui fait foi puisque c'est celui que l'oeil suit.
   *
   * Publié à chaque image et sans filtre, à la différence de la mesure : un
   * abonné qui écrit une position ne veut pas rater la dernière image avant
   * que le ticker se parke, et cette image-là porte souvent le même tick que
   * la précédente. C'est à lui de ne rien faire quand rien ne bouge.
   */
  onFrame(callback: (tick: number) => void): () => void {
    this.frameListeners.add(callback);

    return () => {
      this.frameListeners.delete(callback);
    };
  }

  private publishFrame(): void {
    if (this.frameListeners.size === 0) {
      return;
    }

    const tick = this.getEffectiveTick();

    this.frameListeners.forEach((listener) => listener(tick));
  }

  /** Recalcule la mesure courante et publie si elle a changé. */
  private publishMeasure(): void {
    if (this.measureListeners.size === 0) {
      return;
    }

    // Arrondi obligatoire, pas cosmétique. `seekToTick` mémorise la position
    // en secondes : la relire passe par `tick → tickToSeconds → secondsToTick`,
    // aller-retour qui n'est pas exact en flottant et atterrit volontiers à
    // `débutDeMesure - ε`. Or `getBarAtTick` fait un `Math.floor`, donc le
    // moindre epsilon négatif rend la mesure *précédente* — un clic sur la
    // mesure 9 sélectionnait la 8.
    const tick = Math.round(Math.max(0, this.getEffectiveTick()));
    const bar = this.localTransport.transport.timeline.getBarAtTick(tick) + 1;

    if (bar === this.lastBar) {
      return;
    }

    this.lastBar = bar;
    this.measureListeners.forEach((listener) => listener(bar));
  }

  getPianoComponent(): PianoComponent | null {
    return this.pianoComponent;
  }

  async destroy() {
    this.transportUnsub?.();
    this.transportUnsub = null;

    this.notationUnsub?.();
    this.notationUnsub = null;

    this.pianoUnsub?.();
    this.pianoUnsub = null;

    this.measureListeners.clear();
    this.frameListeners.clear();
    this.recordZoneListeners.clear();

    if (this.app && this.resizeHandler) {
      this.app.renderer.off("resize", this.resizeHandler);
      this.resizeHandler = null;
    }

    await this.minimapComponent?.destroy();
    await this.pianoRollComponent?.destroy();
    await this.pianoComponent?.destroy();

    this.viewNavigator.destroy();

    await this.engine.destroy();

    this.app = null;
    this.pianoComponent = null;
    this.pianoRollComponent = null;
    this.minimapComponent = null;
    this.pianoContainer = null;
    this.pianoRollContainer = null;
    this.minimapContainer = null;
  }
}
